// F-078 — Recettes, dépenses et justificatifs ; onglet « Finances » de la fiche logement.
import { useState } from "react";
import { post, put, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, percent, today } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Stat, Table, Textarea, fieldError } from "@core/ui";
import {
  Amount,
  BuildingSelect,
  DocLink,
  LeaseSelect,
  PAYMENT_METHODS,
  PropertySelect,
  RECOVERABLE,
  YearSelect,
  currentYear,
  isConflict,
  orgApi,
  required,
  useAction,
  useCatalog,
  useRefs,
  type Catalog,
  type Refs,
} from "./shared";

export type Entry = {
  key: string;
  id: string;
  origin: "operation" | "paiement";
  readonly: boolean;
  kind: "recette" | "depense";
  category: string;
  category_label: string;
  label: string;
  amount_cents: number;
  vat_cents: number;
  date: string;
  period_start: string | null;
  period_end: string | null;
  property_id: string | null;
  property_label: string | null;
  building_id: string | null;
  building_label: string | null;
  lease_id: string | null;
  owner_id: string | null;
  counterparty: string;
  recoverable: string;
  recoverable_cents: number;
  document_id: string | null;
  document_filename: string | null;
  justified: boolean;
  source: string;
  contract_id: string | null;
  payment_method: string;
  notes: string;
  cancelled: boolean;
  cancel_reason: string;
};

type Summary = {
  receipts_cents: number;
  expenses_cents: number;
  result_cents: number;
  unallocated_cents: number;
  operations_count: number;
  missing_documents: number;
  by_category: { kind: string; category: string; label: string; total_cents: number; count: number }[];
};

export function OperationsPage() {
  const org = useOrg();
  const refs = useRefs();
  const catalog = useCatalog();
  const [year, setYear] = useState(currentYear());
  const [filters, setFilters] = useState({ property_id: "", kind: "", category: "", missing: false, cancelled: false });
  const params = new URLSearchParams({ year: String(year) });
  if (filters.property_id) params.set("property_id", filters.property_id);
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.category) params.set("category", filters.category);
  if (filters.missing) params.set("missing_document", "true");
  if (filters.cancelled) params.set("include_cancelled", "true");
  const q = useGet<{ items: Entry[]; summary: Summary }>(org.api(`/finance/operations?${params}`));
  const [editing, setEditing] = useState<Entry | "new" | null>(null);
  const [justif, setJustif] = useState<Entry | null>(null);
  const [cancelling, setCancelling] = useState<Entry | null>(null);
  const canEdit = org.can("finance");
  const s = q.data?.summary;
  const cats = (catalog.data?.categories ?? []).filter((c) => !filters.kind || c.kind === filters.kind);

  return (
    <div className="page">
      <PageHeader
        eyebrow="Finances"
        title="Recettes et dépenses"
        intro="Chaque opération est rattachée à un logement ou à un immeuble et justifiée par une pièce. Les loyers encaissés sont repris des paiements enregistrés, sans nouvelle saisie."
        actions={canEdit ? <Button onClick={() => setEditing("new")}>Ajouter une opération</Button> : null}
      />
      <Card>
        <div className="finances-filtres">
          <YearSelect value={year} onChange={setYear} />
          <PropertySelect refs={refs.data} value={filters.property_id} onChange={(v) => setFilters({ ...filters, property_id: v })} empty="Tous les logements" />
          <Select
            label="Type"
            value={filters.kind}
            onChange={(e) => setFilters({ ...filters, kind: e.target.value, category: "" })}
            options={[
              { value: "", label: "Recettes et dépenses" },
              { value: "recette", label: "Recettes" },
              { value: "depense", label: "Dépenses" },
            ]}
          />
          <Select label="Catégorie" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} options={[{ value: "", label: "Toutes" }, ...cats.map((c) => ({ value: c.code, label: c.label }))]} />
          <Checkbox label="Justificatif manquant" checked={filters.missing} onChange={(v) => setFilters({ ...filters, missing: v })} />
          <Checkbox label="Afficher les annulations" checked={filters.cancelled} onChange={(v) => setFilters({ ...filters, cancelled: v })} />
        </div>
      </Card>
      {s ? (
        <div className="stats">
          <Stat label="Recettes encaissées" value={<Amount cents={s.receipts_cents} />} />
          <Stat label="Dépenses payées" value={<Amount cents={s.expenses_cents} />} />
          <Stat label="Solde de trésorerie" value={<Amount cents={s.result_cents} />} hint="Flux de l’année, ni résultat fiscal ni rendement" />
          <Stat label="Opérations sans justificatif" value={s.missing_documents} />
          {s.unallocated_cents > 0 ? <Stat label="Encaissements non ventilés" value={<Amount cents={s.unallocated_cents} />} hint="Dépôt de garantie ou avance ? À ventiler" /> : null}
        </div>
      ) : null}
      <Notice title="Loyers encaissés">Les lignes « Paiement enregistré » viennent des encaissements saisis dans les loyers : elles sont en lecture seule. Un loyer appelé mais impayé n’y figure pas.</Notice>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucune opération pour ces critères.">Ajoutez une dépense ou déposez une facture dans la boîte de réception.</Empty> : null}
      {q.data && q.data.items.length > 0 ? (
        <EntriesTable items={q.data.items} canEdit={canEdit} onEdit={setEditing} onJustif={setJustif} onCancel={setCancelling} />
      ) : null}
      {s && s.by_category.length ? (
        <Card title="Totaux par catégorie">
          <Table caption="Totaux par catégorie">
            <thead>
              <tr>
                <th>Catégorie</th>
                <th>Lignes</th>
                <th className="right">Total</th>
              </tr>
            </thead>
            <tbody>
              {s.by_category.map((c) => (
                <tr key={`${c.kind}-${c.category}`}>
                  <td>
                    {c.label} <span className="finances-sous">{c.kind === "recette" ? "Recette" : "Dépense"}</span>
                  </td>
                  <td>{c.count}</td>
                  <td className="right">
                    <Amount cents={c.total_cents} kind={c.kind as "recette" | "depense"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="small muted">Les totaux sont calculés à partir des mêmes lignes que le détail ci-dessus.</p>
        </Card>
      ) : null}
      {editing ? <OperationModal orgId={org.orgId} entry={editing === "new" ? null : editing} refs={refs.data} catalog={catalog.data} onClose={() => setEditing(null)} /> : null}
      {justif ? <JustifModal orgId={org.orgId} entry={justif} onClose={() => setJustif(null)} /> : null}
      {cancelling ? <CancelModal orgId={org.orgId} entry={cancelling} onClose={() => setCancelling(null)} /> : null}
    </div>
  );
}

export function EntriesTable({ items, canEdit, onEdit, onJustif, onCancel, compact }: { items: Entry[]; canEdit: boolean; onEdit?: (e: Entry) => void; onJustif?: (e: Entry) => void; onCancel?: (e: Entry) => void; compact?: boolean }) {
  return (
    <Table caption="Opérations">
      <thead>
        <tr>
          <th>Date</th>
          <th>Libellé</th>
          <th>Catégorie</th>
          <th className="right">Montant</th>
          {compact ? null : <th>Récupérable</th>}
          <th>Justificatif</th>
          {canEdit && !compact ? <th>
            <span className="sr-only">Actions</span>
          </th> : null}
        </tr>
      </thead>
      <tbody>
        {items.map((e) => (
          <tr key={e.key} className={e.cancelled ? "finances-ligne-annulee" : ""}>
            <td className="num">{date(e.date)}</td>
            <td>
              {e.label}
              <span className="finances-sous">
                {[e.property_label ?? e.building_label, e.counterparty].filter(Boolean).join(" · ") || "Patrimoine général"}
                {e.cancelled ? ` · Annulée : ${e.cancel_reason}` : ""}
              </span>
            </td>
            <td>{e.category_label}</td>
            <td className="right">
              <Amount cents={e.amount_cents} kind={e.kind} />
              {e.vat_cents > 0 ? <span className="finances-sous">dont TVA {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(e.vat_cents / 100)}</span> : null}
            </td>
            {compact ? null : (
              <td>
                {e.kind === "depense" ? (
                  <>
                    {RECOVERABLE[e.recoverable] ?? e.recoverable}
                    {e.recoverable === "partiel" ? <span className="finances-sous">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(e.recoverable_cents / 100)}</span> : null}
                  </>
                ) : (
                  "—"
                )}
              </td>
            )}
            <td>
              {e.origin === "paiement" ? (
                <Badge tone="encre">Paiement enregistré</Badge>
              ) : e.document_id ? (
                <DocLink id={e.document_id} name={e.document_filename} />
              ) : (
                <Badge tone="attention">Justificatif manquant</Badge>
              )}
            </td>
            {canEdit && !compact ? (
              <td>
                {!e.readonly && !e.cancelled ? (
                  <div className="finances-actions">
                    <Button variant="discret" small onClick={() => onJustif?.(e)}>
                      {e.document_id ? "Remplacer la pièce" : "Joindre"}
                    </Button>
                    <Button variant="discret" small onClick={() => onEdit?.(e)}>
                      Modifier
                    </Button>
                    <Button variant="discret" small onClick={() => onCancel?.(e)}>
                      Annuler
                    </Button>
                  </div>
                ) : null}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

type Form = {
  kind: "recette" | "depense";
  category: string;
  label: string;
  amount_cents: number | null;
  vat_cents: number | null;
  operation_on: string;
  property_id: string;
  building_id: string;
  lease_id: string;
  counterparty: string;
  recoverable: string;
  recoverable_cents: number | null;
  payment_method: string;
  notes: string;
  document_id: string | null;
  contract_id: string | null;
};

export function OperationModal({ orgId, entry, refs, catalog, onClose, preset }: { orgId: string; entry: Entry | null; refs?: Refs; catalog?: Catalog; onClose: () => void; preset?: Partial<Form> }) {
  const [f, setF] = useState<Form>(() =>
    entry
      ? {
          kind: entry.kind,
          category: entry.category,
          label: entry.label,
          amount_cents: entry.amount_cents,
          vat_cents: entry.vat_cents || null,
          operation_on: entry.date,
          property_id: entry.property_id ?? "",
          building_id: entry.property_id ? "" : entry.building_id ?? "",
          lease_id: entry.lease_id ?? "",
          counterparty: entry.counterparty,
          recoverable: entry.recoverable,
          recoverable_cents: entry.recoverable_cents || null,
          payment_method: entry.payment_method,
          notes: entry.notes,
          document_id: entry.document_id,
          contract_id: entry.contract_id,
        }
      : {
          kind: "depense",
          category: "",
          label: "",
          amount_cents: null,
          vat_cents: null,
          operation_on: today(),
          property_id: "",
          building_id: "",
          lease_id: "",
          counterparty: "",
          recoverable: "non",
          recoverable_cents: null,
          payment_method: "virement",
          notes: "",
          document_id: null,
          contract_id: null,
          ...preset,
        },
  );
  const a = useAction();
  const duplicate = isConflict(a.error) ? (a.error as Error).message : null;
  const contracts = useGet<{ items: { id: string; supplier: string; kind_label: string; property_label: string | null }[] }>(orgApi(orgId, "/finance/supplier-contracts?status=actif"));
  const cats = (catalog?.categories ?? []).filter((c) => c.kind === f.kind && c.manual);
  const cat = cats.find((c) => c.code === f.category);
  const save = async (confirm: boolean) => {
    const missing = required([
      [!!f.category, "category", "Choisissez une catégorie."],
      [!!f.operation_on, "operation_on", "Indiquez la date de paiement."],
    ]);
    if (missing) return a.setError(missing);
    const body = {
      ...f,
      amount_cents: f.amount_cents ?? 0,
      vat_cents: f.vat_cents ?? 0,
      recoverable_cents: f.recoverable_cents ?? 0,
      property_id: f.property_id || null,
      building_id: f.property_id ? null : f.building_id || null,
      lease_id: f.lease_id || null,
      contract_id: f.kind === "depense" ? f.contract_id || null : null,
      recoverable: f.kind === "recette" ? "non" : f.recoverable,
      confirm_duplicate: confirm,
    };
    const r = await a.run(() => (entry ? put(orgApi(orgId, `/finance/operations/${entry.id}`), body) : post(orgApi(orgId, "/finance/operations"), body)), {
      success: entry ? "Opération modifiée." : "Opération enregistrée.",
      refresh: [orgApi(orgId, "/finance")],
    });
    if (r) onClose();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={entry ? "Modifier l’opération" : "Nouvelle opération"}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>
            Fermer
          </Button>
          {duplicate ? (
            <Button busy={a.busy} onClick={() => save(true)}>
              Enregistrer quand même
            </Button>
          ) : (
            <Button busy={a.busy} onClick={() => save(false)}>
              Enregistrer
            </Button>
          )}
        </>
      }
    >
      <div className="stack">
        {duplicate ? <Notice tone="attention" title="Opération semblable">{duplicate}</Notice> : <ErrorBox error={a.error} />}
        <div className="form-row">
          <Select
            label="Type"
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value as Form["kind"], category: "" })}
            options={[
              { value: "depense", label: "Dépense" },
              { value: "recette", label: "Recette" },
            ]}
          />
          <Select
            label="Catégorie"
            required
            value={f.category}
            error={fieldError(a.error, "category")}
            help={cat?.hint || undefined}
            onChange={(e) => {
              const c = cats.find((x) => x.code === e.target.value);
              setF({ ...f, category: e.target.value, recoverable: c ? (c.default_recoverable === "partiel" ? "a_determiner" : c.default_recoverable) : f.recoverable });
            }}
            options={[{ value: "", label: "Choisir…" }, ...cats.map((c) => ({ value: c.code, label: c.label }))]}
          />
        </div>
        <Input label="Libellé" required value={f.label} error={fieldError(a.error, "label")} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <div className="form-row">
          <MoneyInput label="Montant réglé (TTC)" required cents={f.amount_cents} error={fieldError(a.error, "amount_cents")} onChange={(c) => setF({ ...f, amount_cents: c })} />
          <Input label="Date de paiement" type="date" required value={f.operation_on} error={fieldError(a.error, "operation_on")} onChange={(e) => setF({ ...f, operation_on: e.target.value })} />
          <MoneyInput label="Dont TVA" cents={f.vat_cents} help="Seulement si l’opération est soumise à TVA." error={fieldError(a.error, "vat_cents")} onChange={(c) => setF({ ...f, vat_cents: c })} />
        </div>
        <div className="form-row">
          <PropertySelect refs={refs} value={f.property_id} onChange={(v) => setF({ ...f, property_id: v, lease_id: "" })} />
          {f.property_id ? (
            <LeaseSelect refs={refs} propertyId={f.property_id} value={f.lease_id} onChange={(v) => setF({ ...f, lease_id: v })} empty="Aucun bail précis" />
          ) : (
            <BuildingSelect refs={refs} value={f.building_id} onChange={(v) => setF({ ...f, building_id: v })} empty="Aucun (patrimoine général)" />
          )}
        </div>
        <div className="form-row">
          <Input label={f.kind === "depense" ? "Fournisseur ou organisme" : "Payeur"} value={f.counterparty} onChange={(e) => setF({ ...f, counterparty: e.target.value })} />
          <Select label="Mode de règlement" value={f.payment_method} options={PAYMENT_METHODS} onChange={(e) => setF({ ...f, payment_method: e.target.value })} />
        </div>
        {f.kind === "depense" ? (
          <div className="form-row">
            <Select
              label="Récupérable auprès du locataire"
              value={f.recoverable}
              help="Une dépense de travaux n’est pas automatiquement récupérable : vous décidez."
              options={Object.entries(RECOVERABLE).map(([value, label]) => ({ value, label }))}
              onChange={(e) => setF({ ...f, recoverable: e.target.value })}
            />
            {f.recoverable === "partiel" ? (
              <MoneyInput label="Part récupérable" cents={f.recoverable_cents} error={fieldError(a.error, "recoverable_cents")} onChange={(c) => setF({ ...f, recoverable_cents: c })} />
            ) : null}
          </div>
        ) : null}
        {f.kind === "depense" && (contracts.data?.items.length ?? 0) > 0 ? (
          <Select
            label="Contrat fournisseur (facultatif)"
            value={f.contract_id ?? ""}
            help="Rattacher la facture à son contrat permet de repérer un montant inhabituel."
            onChange={(e) => setF({ ...f, contract_id: e.target.value || null })}
            options={[{ value: "", label: "Aucun" }, ...(contracts.data?.items ?? []).map((c) => ({ value: c.id, label: `${c.supplier} — ${c.kind_label}${c.property_label ? ` (${c.property_label})` : ""}` }))]}
          />
        ) : null}
        <Textarea label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        {!entry ? <p className="small muted">Vous pourrez joindre le justificatif juste après l’enregistrement, depuis la liste.</p> : null}
      </div>
    </Modal>
  );
}

function JustifModal({ orgId, entry, onClose }: { orgId: string; entry: Entry; onClose: () => void }) {
  const a = useAction();
  return (
    <Modal open onClose={onClose} title="Justificatif de l’opération">
      <div className="stack">
        <p>
          <strong>{entry.label}</strong> — <Amount cents={entry.amount_cents} /> le {date(entry.date)}
        </p>
        {entry.document_id ? <Notice>La pièce actuelle est conservée comme version précédente.</Notice> : null}
        <ErrorBox error={a.error} />
        <FileDrop
          busy={a.busy}
          onFiles={async (files) => {
            const form = new FormData();
            form.append("file", files[0]);
            const r = await a.run(() => upload(orgApi(orgId, `/finance/operations/${entry.id}/document`), form), { success: "Justificatif joint.", refresh: [orgApi(orgId, "/finance")] });
            if (r) onClose();
          }}
        />
      </div>
    </Modal>
  );
}

function CancelModal({ orgId, entry, onClose }: { orgId: string; entry: Entry; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title="Annuler l’opération"
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>
            Garder l’opération
          </Button>
          <Button
            variant="danger"
            busy={a.busy}
            onClick={async () => {
              const r = await a.run(() => post(orgApi(orgId, `/finance/operations/${entry.id}/cancel`), { reason }), { success: "Opération annulée.", refresh: [orgApi(orgId, "/finance")] });
              if (r) onClose();
            }}
          >
            Annuler l’opération
          </Button>
        </>
      }
    >
      <div className="stack">
        <p>L’opération reste visible dans l’historique, hors des totaux. Le motif est journalisé.</p>
        <Textarea label="Motif" required value={reason} error={fieldError(a.error, "reason")} onChange={(e) => setReason(e.target.value)} />
        <ErrorBox error={a.error && !fieldError(a.error, "reason") ? a.error : null} />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Onglet « Finances » de la fiche logement

type YieldItem = {
  property_id: string;
  label: string;
  settings: null | { acquisition_price_cents: number | null; acquisition_fees_cents: number; initial_works_cents: number; acquired_on: string | null; estimated_value_cents: number | null; value_source: string };
  current_monthly_rent_cents: number | null;
  theoretical_annual_rent_cents: number | null;
  collected_rent_cents: number;
  owner_charges_cents: number;
  building_share_cents: number;
  vacancy_days: number;
  period_days: number;
  result: { cost_basis_cents: number | null; gross_theoretical_bp: number | null; gross_collected_bp: number | null; net_collected_bp: number | null; net_income_cents: number };
  excluded: { unallocated_cents: number; charges_collected_cents: number; financing_cents: number; investment_cents: number };
  missing: string[];
};

export function YieldCard({ item, orgId, canEdit }: { item: YieldItem; orgId: string; canEdit: boolean }) {
  const [editing, setEditing] = useState(false);
  const r = item.result;
  return (
    <div className="finances-element">
      <div className="finances-element-tete">
        <h3>{item.label}</h3>
        {canEdit ? (
          <Button variant="discret" small onClick={() => setEditing(true)}>
            Données d’acquisition
          </Button>
        ) : null}
      </div>
      <KeyValues
        items={[
          ["Coût d’acquisition", r.cost_basis_cents !== null ? <Amount cents={r.cost_basis_cents} /> : "Non renseigné"],
          ["Rendement brut théorique", r.gross_theoretical_bp !== null ? percent(r.gross_theoretical_bp, 2) : "—"],
          ["Rendement brut constaté", r.gross_collected_bp !== null ? percent(r.gross_collected_bp, 2) : "—"],
          ["Rendement net constaté (avant financement et impôt)", r.net_collected_bp !== null ? percent(r.net_collected_bp, 2) : "—"],
          ["Loyers hors charges encaissés", <Amount cents={item.collected_rent_cents} />],
          ["Charges restant au bailleur", <><Amount cents={item.owner_charges_cents} />{item.building_share_cents ? <span className="finances-sous">dont quote-part d’immeuble <Amount cents={item.building_share_cents} /></span> : null}</>],
          ["Jours sans bail sur la période", `${item.vacancy_days} sur ${item.period_days}`],
          ["Exclus du calcul", <span className="small">Non ventilé <Amount cents={item.excluded.unallocated_cents} /> · charges encaissées <Amount cents={item.excluded.charges_collected_cents} /> · financement <Amount cents={item.excluded.financing_cents} /></span>],
        ]}
      />
      {item.missing.map((m) => (
        <p key={m} className="small muted">
          {m}
        </p>
      ))}
      {editing ? <AcquisitionModal orgId={orgId} item={item} onClose={() => setEditing(false)} /> : null}
    </div>
  );
}

function AcquisitionModal({ orgId, item, onClose }: { orgId: string; item: YieldItem; onClose: () => void }) {
  const s = item.settings;
  const [f, setF] = useState({
    acquisition_price_cents: s?.acquisition_price_cents ?? null,
    acquisition_fees_cents: s?.acquisition_fees_cents ?? null,
    initial_works_cents: s?.initial_works_cents ?? null,
    acquired_on: s?.acquired_on ?? "",
    estimated_value_cents: s?.estimated_value_cents ?? null,
    value_source: s?.value_source ?? "",
  });
  const a = useAction();
  return (
    <Modal
      open
      onClose={onClose}
      title={`Acquisition — ${item.label}`}
      footer={
        <>
          <Button variant="secondaire" onClick={onClose}>
            Fermer
          </Button>
          <Button
            busy={a.busy}
            onClick={async () => {
              const r = await a.run(
                () =>
                  put(orgApi(orgId, `/finance/properties/${item.property_id}/acquisition`), {
                    ...f,
                    acquisition_fees_cents: f.acquisition_fees_cents ?? 0,
                    initial_works_cents: f.initial_works_cents ?? 0,
                    acquired_on: f.acquired_on || null,
                  }),
                { success: "Données d’acquisition enregistrées.", refresh: [orgApi(orgId, "/finance")] },
              );
              if (r) onClose();
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">Ces montants servent uniquement au calcul du rendement. Ils restent des données déclarées par vous.</p>
        <ErrorBox error={a.error} />
        <div className="form-row">
          <MoneyInput label="Prix d’acquisition" cents={f.acquisition_price_cents} error={fieldError(a.error, "acquisition_price_cents")} onChange={(c) => setF({ ...f, acquisition_price_cents: c })} />
          <MoneyInput label="Frais d’acquisition (notaire, agence)" cents={f.acquisition_fees_cents} onChange={(c) => setF({ ...f, acquisition_fees_cents: c })} />
          <MoneyInput label="Travaux initiaux" cents={f.initial_works_cents} onChange={(c) => setF({ ...f, initial_works_cents: c })} />
        </div>
        <div className="form-row">
          <Input label="Date d’acquisition" type="date" value={f.acquired_on} error={fieldError(a.error, "acquired_on")} onChange={(e) => setF({ ...f, acquired_on: e.target.value })} />
          <MoneyInput label="Valeur estimée (hypothèse)" cents={f.estimated_value_cents} onChange={(c) => setF({ ...f, estimated_value_cents: c })} />
          <Input label="Source de l’estimation" value={f.value_source} onChange={(e) => setF({ ...f, value_source: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}

export function PropertyFinanceTab({ orgId, propertyId }: { orgId: string; propertyId: string }) {
  const org = useOrg();
  const [year, setYear] = useState(currentYear());
  const q = useGet<{ items: Entry[]; summary: Summary; yield: YieldItem | null; formulas: string[] }>(orgApi(orgId, `/finance/properties/${propertyId}/summary?year=${year}`));
  const refs = useRefs(orgId);
  const catalog = useCatalog(orgId);
  const [adding, setAdding] = useState(false);
  const canEdit = org.can("finance");
  return (
    <div className="stack">
      <div className="row-between">
        <div style={{ minWidth: 160 }}>
          <YearSelect value={year} onChange={setYear} />
        </div>
        {canEdit ? <Button onClick={() => setAdding(true)}>Ajouter une opération</Button> : null}
      </div>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <>
          <div className="stats">
            <Stat label="Recettes" value={<Amount cents={q.data.summary.receipts_cents} />} />
            <Stat label="Dépenses" value={<Amount cents={q.data.summary.expenses_cents} />} />
            <Stat label="Solde de trésorerie" value={<Amount cents={q.data.summary.result_cents} />} />
            <Stat label="Sans justificatif" value={q.data.summary.missing_documents} />
          </div>
          {q.data.yield ? <YieldCard item={q.data.yield} orgId={orgId} canEdit={canEdit} /> : null}
          {q.data.items.length ? <EntriesTable items={q.data.items} canEdit={false} compact /> : <Empty title="Aucune opération sur ce logement cette année." />}
          <details>
            <summary className="small">Formules du rendement</summary>
            <ul className="finances-formules">
              {q.data.formulas.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </details>
        </>
      ) : null}
      {adding ? <OperationModal orgId={orgId} entry={null} refs={refs.data} catalog={catalog.data} preset={{ property_id: propertyId }} onClose={() => setAdding(false)} /> : null}
    </div>
  );
}
